import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { Search, Tag, Layers, X, DollarSign } from 'lucide-react';
import { opportunitiesService } from '../services/opportunities.service';
import { useOpportunitiesStore } from '../store/opportunities.store';
import type { OpportunitiesFacets } from '../types/opportunity.types';

interface Props {
  /** Filtrado cliente sobre los deals ya cargados (compañía, ID). */
  onClientSearch?: (q: string) => void;
  clientSearch?: string;
  /** Mapa stageId → label legible (de los pipelines de HubSpot). */
  stageLabels?: Map<string, string>;
  /** Mapa pipelineId → label legible. */
  pipelineLabels?: Map<string, string>;
}

export function OpportunitiesFilters({
  onClientSearch,
  clientSearch = '',
  stageLabels,
  pipelineLabels,
}: Props) {
  const { filters, setFilters, resetFilters } = useOpportunitiesStore();
  const [searchInput, setSearchInput] = useState(clientSearch || filters.search || '');
  const [facets, setFacets] = useState<OpportunitiesFacets | null>(null);

  useEffect(() => {
    opportunitiesService
      .facets()
      .then(setFacets)
      .catch(() => {});
  }, []);

  // Debounce search 300ms — dispara filtro backend (name) + filtro cliente
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters({ search: searchInput || undefined });
      onClientSearch?.(searchInput);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, setFilters, onClientSearch]);

  const hasActiveFilters =
    !!filters.search ||
    !!filters.stage?.length ||
    !!filters.pipeline?.length ||
    filters.amountMin !== undefined ||
    filters.amountMax !== undefined;

  const handleClear = () => {
    setSearchInput('');
    onClientSearch?.('');
    resetFilters();
  };

  return (
    <div className="bg-background border-b px-6 py-4 space-y-3">
      {/* Fila 1: búsqueda amplia */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          className="pl-10 h-10"
          placeholder="Buscar por nombre, empresa o ID..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      {/* Fila 2: selects + montos + limpiar */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Stage */}
        <div className="flex items-center gap-1.5 min-w-[180px]">
          <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
          <Select
            value={filters.stage?.[0] ?? 'all'}
            onValueChange={(v) => setFilters({ stage: v === 'all' ? undefined : [v] })}
          >
            <SelectTrigger className="h-9 flex-1">
              <SelectValue placeholder="Todas las etapas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las etapas</SelectItem>
              {facets?.stages.map((s) => (
                <SelectItem key={s} value={s}>
                  {stageLabels?.get(s) ?? s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pipeline */}
        <div className="flex items-center gap-1.5 min-w-[180px]">
          <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
          <Select
            value={filters.pipeline?.[0] ?? 'all'}
            onValueChange={(v) => setFilters({ pipeline: v === 'all' ? undefined : [v] })}
          >
            <SelectTrigger className="h-9 flex-1">
              <SelectValue placeholder="Todos los embudos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los embudos</SelectItem>
              {facets?.pipelines.map((p) => (
                <SelectItem key={p} value={p}>
                  {pipelineLabels?.get(p) ?? p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Monto min */}
        <div className="flex items-center gap-1 w-36">
          <DollarSign className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            type="number"
            className="h-9"
            placeholder="Mín"
            value={filters.amountMin ?? ''}
            onChange={(e) =>
              setFilters({ amountMin: e.target.value ? Number(e.target.value) : undefined })
            }
          />
        </div>

        {/* Monto max */}
        <div className="flex items-center gap-1 w-36">
          <DollarSign className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            type="number"
            className="h-9"
            placeholder="Máx"
            value={filters.amountMax ?? ''}
            onChange={(e) =>
              setFilters({ amountMax: e.target.value ? Number(e.target.value) : undefined })
            }
          />
        </div>

        {/* Limpiar filtros — sólo visible cuando hay algo activo */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="h-9 gap-1.5 text-muted-foreground hover:text-foreground ml-auto"
          >
            <X className="h-3.5 w-3.5" />
            Limpiar filtros
          </Button>
        )}
      </div>
    </div>
  );
}
